import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  HStack,
  VStack,
  Text,
  Icon,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';
import {
  FiChevronDown,
  FiBriefcase,
  FiUser,
  FiCheck
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { useWalletContext } from '../../../context/WalletContext';
import ArtistSwitchConfirmation from './ArtistSwitchConfirmation';

const ArtistSwitcher = ({ artists = [] }) => {
  const [switching, setSwitching] = useState(false);
  const [pendingSwitch, setPendingSwitch] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const toast = useToast();
  const { switchToArtist, switchBackToMerchant, isSwitchedContext, user } = useAuth();
  const cancelRef = React.useRef();



  const handleSwitchToMerchant = () => {
    if (!isSwitchedContext) return;

    setPendingSwitch({ type: 'merchant' });
    onOpen();
  };

  const switchToMerchantView = async () => {
    setSwitching(true);
    try {
      await switchBackToMerchant();

      // Wallet will auto-refresh via WalletContext useEffect

      toast({
        title: 'Switched to Merchant View',
        description: 'You are now viewing your merchant dashboard',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Error switching to merchant:', error);
      toast({
        title: 'Switch Failed',
        description: 'Failed to switch to merchant view',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSwitching(false);
      onClose();
      setPendingSwitch(null);
    }
  };

  const handleSwitchToArtist = (artist) => {
    const artistId = artist.artist_id || artist.user_id || artist.id;
    // Check if already viewing this artist
    if (isSwitchedContext && user?.currentContext?.artist_id === artistId) return;

    setPendingSwitch({ type: 'artist', artist });
    onOpen();
  };

  const switchToArtistView = async (artist) => {
    setSwitching(true);
    try {
      const artistId = artist.artist_id || artist.user_id || artist.id;
      await switchToArtist(artistId, artist);

      // Wallet will auto-refresh via WalletContext useEffect

      // Redirect to artist dashboard after successful switch
      navigate('/dashboard/artist/tracks');

      toast({
        title: `Switched to ${artist.artist_name}`,
        description: 'You are now viewing the artist perspective',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Error switching to artist:', error);
      toast({
        title: 'Switch Failed',
        description: `Failed to switch to ${artist.artist_name}`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSwitching(false);
      onClose();
      setPendingSwitch(null);
    }
  };

  const handleConfirmSwitch = () => {
    if (!pendingSwitch) return;

    if (pendingSwitch.type === 'merchant') {
      switchToMerchantView();
    } else if (pendingSwitch.type === 'artist') {
      switchToArtistView(pendingSwitch.artist);
    }
  };

  const handleCancelSwitch = () => {
    setPendingSwitch(null);
    onClose();
  };

  // Show switcher for merchants only, and hide when switched to artist context
  if (user?.account_type !== 'merchant' || isSwitchedContext) {
    return null;
  }

  const getCurrentIcon = () => {
    return isSwitchedContext ? FiUser : FiBriefcase;
  };


  return (
    <Menu placement="bottom-end">
      <MenuButton
        as={Button}
        rightIcon={<Icon as={FiChevronDown} boxSize={4} />}
        size="md"
        variant="ghost"
        color="primary.900"
        fontWeight="medium"
        _hover={{ bg: "secondary.900", color: "primary.100" }}
        _active={{ bg: "secondary.700" }}
        isLoading={switching}
        loadingText="Switching..."
        px={3}
      >
        <HStack spacing={2}>
          <Icon as={getCurrentIcon()} boxSize={4} />
          <Text fontSize="sm" noOfLines={1}>
            Switch View
          </Text>
        </HStack>
      </MenuButton>

      <MenuList
        minW="280px"
        maxW="320px"
        bg="dark.500"
        borderColor="secondary.900"
        borderWidth="1px"
        shadow="xl"
        py={2}
      >
        {/* Merchant Dashboard Option */}
        <MenuItem
          onClick={handleSwitchToMerchant}
          isDisabled={!isSwitchedContext || switching}
          _hover={{ bg: 'secondary.700' }}
          color="primary.100"
          py={3}
          px={4}
        >
          <HStack w="full" spacing={3}>
            <Box
              p={2}
              rounded="md"
              bg={!isSwitchedContext ? "dark.900" : "secondary.900"}
              transition="all 0.2s"
            >
              <Icon as={FiBriefcase} boxSize={4} color="primary.100" />
            </Box>
            <VStack align="start" spacing={0} flex={1}>
              <HStack>
                <Text fontSize="sm" fontWeight="semibold" color="primary.100">
                  Merchant
                </Text>
                {!isSwitchedContext && (
                  <Icon as={FiCheck} color="green.400" boxSize={3.5} />
                )}
              </HStack>
              <Text fontSize="xs" color="primary.900">
                Business overview
              </Text>
            </VStack>
          </HStack>
        </MenuItem>

        {artists.length > 0 && <MenuDivider borderColor="secondary.900" my={2} />}

        {/* Artists List */}
        {artists.length > 0 && (
          <Box px={4} py={2}>
            <Text fontSize="xs" color="primary.900" fontWeight="semibold" letterSpacing="wide">
              Artists ({artists.length})
            </Text>
          </Box>
        )}

        {artists.length === 0 ? (
          <MenuItem isDisabled bg="transparent" py={3} px={4}>
            <Text fontSize="sm" color="primary.900">
              No artists yet
            </Text>
          </MenuItem>
        ) : (
          <Box maxH="300px" overflowY="auto" css={{
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'rgba(255, 255, 255, 0.3)',
            },
          }}>
            {artists.map((artist) => {
              const artistId = artist.artist_id || artist.user_id || artist.id;
              const isActive = isSwitchedContext && user?.currentContext?.artist_id === artistId;
              return (
                <Box
                  key={artistId}
                  px={4}
                  py={3}
                  _hover={{ bg: 'secondary.700' }}
                  transition="all 0.2s"
                  borderRadius="md"
                  cursor="default"
                >
                  <HStack w="full" spacing={3}>
                    <Box
                      p={2}
                      rounded="md"
                      bg={isActive ? "dark.900" : "secondary.900"}
                      transition="all 0.2s"
                    >
                      <Icon as={FiUser} boxSize={4} color="primary.100" />
                    </Box>
                    <VStack align="start" spacing={0} flex={1} minW={0}>
                      <HStack spacing={2} w="full">
                        <Text fontSize="sm" fontWeight="semibold" noOfLines={1} color="primary.100">
                          {artist.artist_name}
                        </Text>
                        {isActive && (
                          <Icon as={FiCheck} color="green.400" boxSize={3.5} flexShrink={0} />
                        )}
                      </HStack>
                      <Text fontSize="xs" color="primary.900" noOfLines={1}>
                        {artist.track_count || 0} tracks
                      </Text>
                    </VStack>
                    {!isActive && (
                      <Button
                        size="xs"
                        variant="brand"
                        onClick={() => handleSwitchToArtist(artist)}
                        isDisabled={switching}
                        isLoading={switching}
                        flexShrink={0}
                        px={3}
                        _hover={{
                          transform: "translateY(-1px)",
                          boxShadow: "sm"
                        }}
                        transition="all 0.2s"
                      >
                        Switch
                      </Button>
                    )}
                  </HStack>
                </Box>
              );
            })}
          </Box>
        )}
      </MenuList>

      {/* Confirmation Dialog */}
      {pendingSwitch?.type === 'artist' && (
        <ArtistSwitchConfirmation
          isOpen={isOpen}
          onClose={handleCancelSwitch}
          artist={pendingSwitch.artist}
          onConfirm={handleConfirmSwitch}
          isLoading={switching}
          cancelRef={cancelRef}
        />
      )}
    </Menu>
  );
};

export default ArtistSwitcher;